import { useEffect, useState } from 'react';

/** Kept in step with core/paging.py's PAGE_SIZES — the server clamps `limit`
 *  to these, so offering a fourth option here would silently serve a
 *  different number of rows than the one the dispatcher picked. */
export const PAGE_SIZES = [25, 50, 100];

/**
 * The search term, page size and offset every paginated table needs, in one
 * call.
 *
 * Resetting the offset whenever the search term changes is the non-obvious
 * half. Without it, searching from page 4 asks the server for rows 76-100 of
 * a three-row result set, and the dispatcher gets an empty table for a query
 * that plainly matches something.
 */
export function usePagedList() {
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZES[0]);
  const [offset, setOffset] = useState(0);

  const onSearchChange = (term) => {
    setSearch(term);
    setOffset(0);
  };

  return { search, onSearchChange, limit, setLimit, offset, setOffset };
}

/**
 * V-296. `offset` is local state; `total` moves on its own whenever a row
 * disappears from underneath the page being viewed — a delete or archive
 * this browser just did, or a colleague's, arriving as an SSE-driven
 * refetch. Neither ever touches `offset`, the same gap V-233 fixed for a
 * FILTER change (a filter is applied by the dispatcher, so there was an
 * onChange handler to attach setOffset(0) to) — a shrinking total has no
 * such event to hook, only the query settling with fewer rows than the
 * page it's serving.
 *
 * Without this, deleting the last row of a later page asks the server for
 * an offset past the end of the new, smaller total, gets 0 rows back, and
 * the dispatcher reads a false "nothing here at all" over a table that
 * still has rows one page back — plus PaginationFooter's own
 * `first`/`last` math going nonsensical (e.g. "26–25 iš 25").
 *
 * Steps back exactly ONE page rather than resetting to 0, so a delete from
 * page 3 lands on page 2, not page 1 — the same "smallest correction that
 * restores validity" the search-term reset above does NOT need (a fresh
 * search has no page worth preserving; a shrunk one usually does).
 *
 * Call once per paginated list, after its query resolves `total` — see
 * ServiceHistoryPanel.jsx for the wiring.
 */
export function useOffsetClamp(total, limit, offset, setOffset) {
  useEffect(() => {
    // total === undefined: the query hasn't resolved yet (still loading, or
    // this list has no page 1 result to compare against). Nothing to
    // correct until a real count exists.
    if (total !== undefined && offset > 0 && offset >= total) {
      setOffset(Math.max(0, offset - limit));
    }
  }, [total, limit, offset, setOffset]);
}
